import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Reports.css';
import API_URL from '../config/api';

const Reports = () => {
  const [activeTab, setActiveTab] = useState('aging');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Report data states
  const [agingData, setAgingData] = useState(null);
  const [openInvoicesData, setOpenInvoicesData] = useState(null);
  const [revenueData, setRevenueData] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [clientPaymentData, setClientPaymentData] = useState(null);
  
  // Filters
  const [selectedClient, setSelectedClient] = useState('');
  const [clients, setClients] = useState([]);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [groupBy, setGroupBy] = useState('month');

  useEffect(() => {
    loadClients();
    loadDashboard();
  }, []);

  useEffect(() => {
    if (activeTab === 'aging') loadAgingReport();
    else if (activeTab === 'open') loadOpenInvoices();
    else if (activeTab === 'revenue') loadRevenueReport();
    else if (activeTab === 'payment') loadClientPaymentHistory();
  }, [activeTab, selectedClient, dateRange, groupBy]);

  const loadClients = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data.clients || []);
    } catch (err) {
      console.error('Error loading clients:', err);
    }
  };

  const loadAgingReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const params = selectedClient ? `?clientId=${selectedClient}` : '';
      const response = await axios.get(`${API_URL}/api/reports/aging-report${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgingData(response.data);
    } catch (err) {
      setError('Failed to load aging report');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadOpenInvoices = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const params = selectedClient ? `?clientId=${selectedClient}` : '';
      const response = await axios.get(`${API_URL}/api/reports/open-invoices${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOpenInvoicesData(response.data);
    } catch (err) {
      setError('Failed to load open invoices');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadRevenueReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/reports/revenue?startDate=${dateRange.start}&endDate=${dateRange.end}&groupBy=${groupBy}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRevenueData(response.data);
    } catch (err) {
      setError('Failed to load revenue report');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadDashboard = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/reports/invoice-dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardData(response.data);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
    }
  };

  const loadClientPaymentHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const params = selectedClient ? `?clientId=${selectedClient}` : '';
      const response = await axios.get(`${API_URL}/api/reports/client-payment-history${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClientPaymentData(response.data);
    } catch (err) {
      setError('Failed to load client payment history');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US');
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      open: 'status-badge-warning',
      submitted: 'status-badge-info',
      paid: 'status-badge-success',
      overdue: 'status-badge-danger',
      cancelled: 'status-badge-secondary'
    };
    return statusClasses[status] || 'status-badge-secondary';
  };

  const exportToCSV = (data, filename) => {
    // Simple CSV export implementation
    const csv = data.map(row => Object.values(row).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.csv`;
    a.click();
  };

  return (
    <div className="reports-container">
      <h1 className="page-title">Financial Reports</h1>

      {/* Dashboard Summary Cards */}
      {dashboardData && dashboardData.statusStats && (
        <div className="dashboard-cards">
          {dashboardData.statusStats.map(stat => (
            <div key={stat.status} className="dashboard-card">
              <div className="card-header">
                <span className={`status-badge ${getStatusBadge(stat.status)}`}>
                  {stat.status.toUpperCase()}
                </span>
              </div>
              <div className="card-body">
                <div className="card-stat">
                  <span className="stat-value">{stat.count}</span>
                  <span className="stat-label">Invoices</span>
                </div>
                <div className="card-stat">
                  <span className="stat-value">{formatCurrency(stat.total_amount)}</span>
                  <span className="stat-label">Total</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="tabs">
        <button
          className={`tab ${activeTab === 'aging' ? 'active' : ''}`}
          onClick={() => setActiveTab('aging')}
        >
          AR Aging
        </button>
        <button
          className={`tab ${activeTab === 'open' ? 'active' : ''}`}
          onClick={() => setActiveTab('open')}
        >
          Open Invoices
        </button>
        <button
          className={`tab ${activeTab === 'revenue' ? 'active' : ''}`}
          onClick={() => setActiveTab('revenue')}
        >
          Revenue
        </button>
        <button
          className={`tab ${activeTab === 'payment' ? 'active' : ''}`}
          onClick={() => setActiveTab('payment')}
        >
          Client Payments
        </button>
      </div>

      {/* Filters */}
      <div className="filters">
        {(activeTab === 'aging' || activeTab === 'open' || activeTab === 'payment') && (
          <div className="filter-group">
            <label>Filter by Client:</label>
            <select 
              value={selectedClient} 
              onChange={(e) => setSelectedClient(e.target.value)}
            >
              <option value="">All Clients</option>
              {clients.map(client => (
                <option key={client.id} value={client.id}>{client.name}</option>
              ))}
            </select>
          </div>
        )}

        {activeTab === 'revenue' && (
          <>
            <div className="filter-group">
              <label>Start Date:</label>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              />
            </div>
            <div className="filter-group">
              <label>End Date:</label>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              />
            </div>
            <div className="filter-group">
              <label>Group By:</label>
              <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
                <option value="month">Month</option>
                <option value="quarter">Quarter</option>
                <option value="year">Year</option>
              </select>
            </div>
          </>
        )}
      </div>

      {/* Loading and Error States */}
      {loading && <div className="loading">Loading report data...</div>}
      {error && <div className="error-message">{error}</div>}

      {/* Report Content */}
      {!loading && !error && (
        <div className="report-content">
          {/* AR Aging Report */}
          {activeTab === 'aging' && agingData && (
            <div className="aging-report">
              <h2>Accounts Receivable Aging Report</h2>
              
              <div className="aging-summary">
                <div className="aging-bucket">
                  <h3>Current (0-15 Days)</h3>
                  <p className="bucket-amount">{formatCurrency(agingData.agingBuckets.current.total)}</p>
                  <p className="bucket-count">{agingData.agingBuckets.current.invoices.length} invoices</p>
                </div>
                <div className="aging-bucket warning">
                  <h3>15-30 Days</h3>
                  <p className="bucket-amount">{formatCurrency(agingData.agingBuckets.days_15_30.total)}</p>
                  <p className="bucket-count">{agingData.agingBuckets.days_15_30.invoices.length} invoices</p>
                </div>
                <div className="aging-bucket danger">
                  <h3>30-45 Days</h3>
                  <p className="bucket-amount">{formatCurrency(agingData.agingBuckets.days_30_45.total)}</p>
                  <p className="bucket-count">{agingData.agingBuckets.days_30_45.invoices.length} invoices</p>
                </div>
                <div className="aging-bucket critical">
                  <h3>45+ Days</h3>
                  <p className="bucket-amount">{formatCurrency(agingData.agingBuckets.days_45_plus.total)}</p>
                  <p className="bucket-count">{agingData.agingBuckets.days_45_plus.invoices.length} invoices</p>
                </div>
              </div>

              <div className="grand-total">
                <h3>Total Outstanding: {formatCurrency(agingData.grandTotal)}</h3>
              </div>

              {agingData.clientTotals.length > 0 && (
                <div className="client-totals">
                  <h3>Outstanding by Client</h3>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Client</th>
                        <th>Invoice Count</th>
                        <th>Total Outstanding</th>
                      </tr>
                    </thead>
                    <tbody>
                      {agingData.clientTotals.map(client => (
                        <tr key={client.client_id}>
                          <td>{client.client_name}</td>
                          <td>{client.count}</td>
                          <td>{formatCurrency(client.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Open Invoices Report */}
          {activeTab === 'open' && openInvoicesData && (
            <div className="open-invoices-report">
              <div className="report-header">
                <h2>Open Invoices Report</h2>
                <button 
                  className="btn-secondary"
                  onClick={() => exportToCSV(openInvoicesData.invoices, 'open-invoices')}
                >
                  Export to CSV
                </button>
              </div>

              <div className="summary-stats">
                <div className="stat-card">
                  <span className="stat-label">Total Open</span>
                  <span className="stat-value">{openInvoicesData.summary.count}</span>
                </div>
                <div className="stat-card">
                  <span className="stat-label">Total Amount</span>
                  <span className="stat-value">{formatCurrency(openInvoicesData.summary.totalAmount)}</span>
                </div>
                <div className="stat-card">
                  <span className="stat-label">Overdue</span>
                  <span className="stat-value danger">{formatCurrency(openInvoicesData.summary.overdueAmount)}</span>
                </div>
                <div className="stat-card">
                  <span className="stat-label">Current</span>
                  <span className="stat-value success">{formatCurrency(openInvoicesData.summary.currentAmount)}</span>
                </div>
              </div>

              <table className="data-table">
                <thead>
                  <tr>
                    <th>Invoice #</th>
                    <th>Client</th>
                    <th>Invoice Date</th>
                    <th>Due Date</th>
                    <th>Days Outstanding</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {openInvoicesData.invoices.map(invoice => (
                    <tr key={invoice.id}>
                      <td>{invoice.invoice_number}</td>
                      <td>{invoice.client_name}</td>
                      <td>{formatDate(invoice.invoice_date)}</td>
                      <td>{formatDate(invoice.due_date)}</td>
                      <td className={parseInt(invoice.days_outstanding) > 0 ? 'danger' : ''}>
                        {invoice.days_outstanding} days
                      </td>
                      <td>{formatCurrency(invoice.total)}</td>
                      <td>
                        <span className={`status-badge ${getStatusBadge(invoice.status)}`}>
                          {invoice.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Revenue Report */}
          {activeTab === 'revenue' && revenueData && (
            <div className="revenue-report">
              <h2>Revenue Report</h2>
              <p className="date-range">
                {formatDate(revenueData.period.start)} - {formatDate(revenueData.period.end)}
              </p>

              <div className="revenue-summary">
                <div className="stat-card large">
                  <span className="stat-label">Total Revenue (Paid)</span>
                  <span className="stat-value">{formatCurrency(revenueData.summary.total_paid)}</span>
                </div>
                <div className="stat-card large">
                  <span className="stat-label">Outstanding</span>
                  <span className="stat-value">{formatCurrency(revenueData.summary.total_outstanding)}</span>
                </div>
                <div className="stat-card large">
                  <span className="stat-label">Average Invoice</span>
                  <span className="stat-value">{formatCurrency(revenueData.summary.avg_paid_amount)}</span>
                </div>
                <div className="stat-card large">
                  <span className="stat-label">Total Invoices</span>
                  <span className="stat-value">{revenueData.summary.total_invoices}</span>
                </div>
              </div>

              {revenueData.revenueByPeriod.length > 0 && (
                <div className="revenue-by-period">
                  <h3>Revenue by {revenueData.period.groupBy}</h3>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Period</th>
                        <th>Invoices</th>
                        <th>Total Revenue</th>
                        <th>Avg Invoice</th>
                      </tr>
                    </thead>
                    <tbody>
                      {revenueData.revenueByPeriod.map(period => (
                        <tr key={period.period}>
                          <td>{period.period}</td>
                          <td>{period.invoice_count}</td>
                          <td>{formatCurrency(period.total_revenue)}</td>
                          <td>{formatCurrency(period.avg_invoice_amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {revenueData.topClients.length > 0 && (
                <div className="top-clients">
                  <h3>Top 10 Clients by Revenue</h3>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Client</th>
                        <th>Invoices</th>
                        <th>Total Revenue</th>
                        <th>Avg Invoice</th>
                      </tr>
                    </thead>
                    <tbody>
                      {revenueData.topClients.map(client => (
                        <tr key={client.client_id}>
                          <td>{client.client_name}</td>
                          <td>{client.invoice_count}</td>
                          <td>{formatCurrency(client.total_revenue)}</td>
                          <td>{formatCurrency(client.avg_invoice_amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Client Payment History */}
          {activeTab === 'payment' && clientPaymentData && (
            <div className="client-payment-report">
              <h2>Client Payment History</h2>

              {clientPaymentData.slowPayers && clientPaymentData.slowPayers.length > 0 && (
                <div className="alert alert-warning">
                  <h3>⚠️ Attention Required</h3>
                  <p>{clientPaymentData.slowPayers.length} clients have overdue payments or slow payment patterns</p>
                </div>
              )}

              <div className="payment-summary">
                <div className="stat-card">
                  <span className="stat-label">Total Clients</span>
                  <span className="stat-value">{clientPaymentData.summary.totalClients}</span>
                </div>
                <div className="stat-card">
                  <span className="stat-label">Slow Payers</span>
                  <span className="stat-value danger">{clientPaymentData.summary.slowPayersCount}</span>
                </div>
                <div className="stat-card">
                  <span className="stat-label">Total Outstanding</span>
                  <span className="stat-value">{formatCurrency(clientPaymentData.summary.totalOutstanding)}</span>
                </div>
              </div>

              <table className="data-table">
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Total Invoices</th>
                    <th>Paid</th>
                    <th>Overdue</th>
                    <th>Total Billed</th>
                    <th>Outstanding</th>
                    <th>Avg Days to Pay</th>
                    <th>Last Invoice</th>
                  </tr>
                </thead>
                <tbody>
                  {clientPaymentData.clients.map(client => (
                    <tr key={client.client_id} className={client.overdue_invoices > 0 ? 'highlight-warning' : ''}>
                      <td>{client.client_name}</td>
                      <td>{client.total_invoices}</td>
                      <td>{client.paid_invoices}</td>
                      <td className={client.overdue_invoices > 0 ? 'danger' : ''}>{client.overdue_invoices}</td>
                      <td>{formatCurrency(client.total_billed)}</td>
                      <td>{formatCurrency(client.total_outstanding)}</td>
                      <td className={parseFloat(client.avg_days_to_pay) > 30 ? 'warning' : ''}>
                        {client.avg_days_to_pay ? Math.round(parseFloat(client.avg_days_to_pay)) : 'N/A'} days
                      </td>
                      <td>{formatDate(client.last_invoice_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {clientPaymentData.paymentTimeline && clientPaymentData.paymentTimeline.length > 0 && (
                <div className="payment-timeline">
                  <h3>Payment Timeline for Selected Client</h3>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Invoice #</th>
                        <th>Invoice Date</th>
                        <th>Due Date</th>
                        <th>Paid Date</th>
                        <th>Amount</th>
                        <th>Days to Payment</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clientPaymentData.paymentTimeline.map(invoice => (
                        <tr key={invoice.id}>
                          <td>{invoice.invoice_number}</td>
                          <td>{formatDate(invoice.invoice_date)}</td>
                          <td>{formatDate(invoice.due_date)}</td>
                          <td>{formatDate(invoice.paid_at)}</td>
                          <td>{formatCurrency(invoice.total)}</td>
                          <td>{invoice.days_to_payment ? Math.round(parseFloat(invoice.days_to_payment)) : 'N/A'}</td>
                          <td>
                            <span className={`status-badge ${getStatusBadge(invoice.status)}`}>
                              {invoice.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Reports;
